import React, { forwardRef } from "react";
import { LayoutChangeEvent, Platform, StyleProp, Text, View, ViewStyle } from "react-native";
import { Icon as FontAwesomeIcon } from "./IconRoot";
import { fas } from "@fortawesome/pro-solid-svg-icons";
import { fal } from "@fortawesome/pro-light-svg-icons";
import { library, IconName as FaIconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";
import CheckSvg from "../assets/icons/check.svg";
import HumanAndChildHoldingHandsSvg from "../assets/icons/human-and-child-holdinghands.svg";

const IconNameList = [
    "__empty__",
    "__none__",
    "share",
    "close",
    "back",
    "menu",
    "add",
    "add-large",
    "square-add",
    "square",
    "square-check",
    "colleagues",
    "right-left",
    "chevron-right",
    "chevron-left",
    "chevron-down",
    "check",
    "check-heavy",
    "badge-check",
    "check-solid",
    "check-circle",
    "check-circle-heavy",
    "uncheck-circle",
    "arrow-right",
    "arrow-right-from-bracket",
    "arrow-turn-up",
    "arrow-turn-down",
    "company",
    "building",
    "error",
    "error-heavy",
    "wait",
    "edit",
    "age",
    "training",
    "experience",
    "file",
    "file-lines",
    "calendar",
    "calendar-clock",
    "send",
    "swipe-left",
    "swipe-right",
    "trash",
    "work",
    "working-time",
    "holiday",
    "vacation",
    "umbrella",
    "military",
    "sickness",
    "signature",
    "accident",
    "maternity",
    "user",
    "user-male",
    "user-female",
    "user-vneck",
    "camera",
    "email",
    "private-email",
    "house",
    "external-link",
    "phone-call",
    "map-marker",
    "cash",
    "dollar-square",
    "onboarding",
    "duration",
    "clock",
    "talent",
    "talents",
    "gender-female",
    "gender-male",
    "mission",
    "invoice",
    "todo",
    "salary",
    "timesheet",
    "help",
    "help-circle",
    "circle-question",
    "assistance",
    "ask-assistance",
    "articles",
    "logout",
    "search",
    "fee-private-transportation",
    "fee-food",
    "fee-public-transportation",
    "fee-phone",
    "fee-remote-work",
    "fee-other",
    "snackbar-info",
    "snackbar-error",
    "hourglass",
    "images",
    "globe",
    "folder",
    "square-user",
    "bank-account",
    "civil-state",
    "lpp",
    "cct",
    "children",
    "upload",
    "cloud-upload",
    "cloud-upload-outline",
    "cancel-mission",
    "wifi-slash",
    "address-card",
    "passport",
    "work-permit",
    "receipt",
    "hotel_clothes",
    "hotel_apron",
    "info",
    "payroll_breakdown",
    "tag",
    "buildings",
    "hotel",
    "ijm",
    "activities",
    "phone",
    "circle",
    "lightbulb-on",
    "lightbulb",
    "fairy",
    "legal_employer",
    "write",
    "marriage",
    "moving",
    "death_close_relative",
    "sickness_close_relative",
    "gear",
] as const;
export type IconName = (typeof IconNameList)[number];

type IconProps = {
    name: IconName;
    size?: number;
    style?: StyleProp<ViewStyle>;
    color?: string;
    iconPadding?: number; // padding added to internal icon
    onLayout?: (event: LayoutChangeEvent) => void;
};

const EmptyIcon: React.FC<{ size?: number }> = ({ size = 16 }) => {
    return <View style={{ width: size, height: size }} />;
};

const NoneIcon: React.FC<{ size?: number }> = () => {
    return null;
};

const CheckIconComponent: React.FC<{ size?: number; color?: string }> = ({
    size = 16,
    color = undefined,
}) => {
    return (
        <View style={{ width: size, height: size }}>
            <CheckSvg width={size} height={size} fill={color} style={{ color } as ViewStyle} />
        </View>
    );
};

const HumanAndChildHoldingHandsComponent: React.FC<{ size?: number; color?: string }> = ({
    size = 16,
    color = undefined,
}) => {
    return (
        <View style={{ width: size, height: size }}>
            <HumanAndChildHoldingHandsSvg
                width={size}
                height={size}
                fill={color}
                style={{ color } as ViewStyle}
            />
        </View>
    );
};

const WorkPermitIconComponent: React.FC<{ size?: number; color?: string }> = ({
    size = 16,
    color = undefined,
}) => {
    return (
        <View
            style={{
                position: "relative",
                width: size,
                height: size,
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Icon name="file" size={size} color={color} />
            <Text style={{ position: "absolute", color, bottom: 1, fontSize: (size * 14) / 32 }}>
                C
            </Text>
        </View>
    );
};

type IconComponent = typeof FontAwesomeIcon | typeof EmptyIcon | typeof CheckIconComponent;

const NAME_MAP: Record<IconName, [IconComponent, FaIconName, number?, IconPrefix?]> = {
    back: [FontAwesomeIcon, "arrow-left"],
    close: [FontAwesomeIcon, "close"],
    share: [FontAwesomeIcon, "share"],
    menu: [FontAwesomeIcon, "ellipsis"],
    add: [FontAwesomeIcon, "plus"],
    "add-large": [FontAwesomeIcon, "plus-large"],
    "square-add": [FontAwesomeIcon, "square-plus"],
    square: [FontAwesomeIcon, "square", 1.0, "fal"],
    "square-check": [FontAwesomeIcon, "square-check", 1.0, "fas"],
    colleagues: [FontAwesomeIcon, "screen-users"],
    "right-left": [FontAwesomeIcon, "right-left", 1.0],
    "chevron-right": [FontAwesomeIcon, "chevron-right"],
    "chevron-left": [FontAwesomeIcon, "chevron-left"],
    "chevron-down": [FontAwesomeIcon, "chevron-down", 0.75],
    check: [FontAwesomeIcon, "check"],
    "check-solid": [FontAwesomeIcon, "check", 1, "fas"],
    "check-heavy": [CheckIconComponent, "badge-check", 0.8],
    "badge-check": [FontAwesomeIcon, "badge-check"],
    "error-heavy": [FontAwesomeIcon, "xmark", 1.1, "fas"],
    "check-circle": [FontAwesomeIcon, "check-circle"],
    "check-circle-heavy": [FontAwesomeIcon, "check-circle", 1, "fas"],
    "uncheck-circle": [FontAwesomeIcon, "circle"],
    "arrow-right": [FontAwesomeIcon, "arrow-right"],
    "arrow-right-from-bracket": [FontAwesomeIcon, "arrow-right-from-bracket"],
    "arrow-turn-up": [FontAwesomeIcon, "arrow-turn-up"],
    "arrow-turn-down": [FontAwesomeIcon, "arrow-turn-down"],
    company: [FontAwesomeIcon, "buildings"],
    building: [FontAwesomeIcon, "building"],
    error: [FontAwesomeIcon, "triangle-exclamation"],
    wait: [FontAwesomeIcon, "ellipsis"],
    edit: [FontAwesomeIcon, "pencil"],
    age: [FontAwesomeIcon, "person"],
    training: [FontAwesomeIcon, "graduation-cap"],
    experience: [FontAwesomeIcon, "medal"],
    file: [FontAwesomeIcon, "file"],
    "file-lines": [FontAwesomeIcon, "file-lines"],
    calendar: [FontAwesomeIcon, "calendar"],
    "calendar-clock": [FontAwesomeIcon, "calendar-clock"],
    send: [FontAwesomeIcon, "send"],
    "swipe-left": [FontAwesomeIcon, "chevron-double-left", 1.0, "fas"],
    "swipe-right": [FontAwesomeIcon, "chevron-double-right", 1.0, "fas"],
    trash: [FontAwesomeIcon, "trash-can"],
    work: [FontAwesomeIcon, "briefcase"],
    holiday: [FontAwesomeIcon, "calendar-star"],
    vacation: [FontAwesomeIcon, "umbrella-beach"],
    umbrella: [FontAwesomeIcon, "umbrella"],
    military: [FontAwesomeIcon, "person-military-rifle"],
    sickness: [FontAwesomeIcon, "face-thermometer"],
    signature: [FontAwesomeIcon, "file-signature"],
    accident: [FontAwesomeIcon, "face-head-bandage"],
    maternity: [FontAwesomeIcon, "baby-carriage"],
    user: [FontAwesomeIcon, "user"],
    "user-male": [FontAwesomeIcon, "user-tie-hair"],
    "user-female": [FontAwesomeIcon, "user-tie-hair-long"],
    "user-vneck": [FontAwesomeIcon, "user-vneck"],
    camera: [FontAwesomeIcon, "camera"],
    "external-link": [FontAwesomeIcon, "arrow-up-right"],
    "phone-call": [FontAwesomeIcon, "phone-arrow-up-right"],
    email: [FontAwesomeIcon, "envelope", 1.0, "fal"],
    "private-email": [FontAwesomeIcon, "envelope"],
    house: [FontAwesomeIcon, "house"],
    "map-marker": [FontAwesomeIcon, "map-marker"],
    "working-time": [FontAwesomeIcon, "calendar-clock"],
    cash: [FontAwesomeIcon, "sack-dollar"],
    "dollar-square": [FontAwesomeIcon, "dollar-square"],
    onboarding: [FontAwesomeIcon, "clipboard-list"],
    duration: [FontAwesomeIcon, "calendar-range"],
    clock: [FontAwesomeIcon, "clock"],
    talent: [FontAwesomeIcon, "user", 0.8],
    talents: [FontAwesomeIcon, "users"],
    "gender-male": [FontAwesomeIcon, "mars"],
    "gender-female": [FontAwesomeIcon, "venus"],
    mission: [FontAwesomeIcon, "suitcase", 0.85, "fal"],
    invoice: [FontAwesomeIcon, "file-invoice", 0.85, "fal"],
    todo: [FontAwesomeIcon, "list-check", 0.85],
    salary: [FontAwesomeIcon, "file-invoice-dollar"],
    timesheet: [FontAwesomeIcon, "stopwatch"],
    help: [FontAwesomeIcon, "square-question"],
    assistance: [FontAwesomeIcon, "message-question", 1, "fal"],
    "ask-assistance": [FontAwesomeIcon, "comment-plus", 1],
    articles: [FontAwesomeIcon, "book-font", 1],
    logout: [FontAwesomeIcon, "right-from-bracket", 1, "fal"],
    info: [FontAwesomeIcon, "info", 1, "fas"],
    "help-circle": [FontAwesomeIcon, "circle-info"],
    "circle-question": [FontAwesomeIcon, "circle-question", 0.8],
    search: [FontAwesomeIcon, "magnifying-glass"],
    "fee-private-transportation": [FontAwesomeIcon, "car"],
    "fee-food": [FontAwesomeIcon, "plate-utensils"],
    "fee-public-transportation": [FontAwesomeIcon, "train"],
    "fee-phone": [FontAwesomeIcon, "phone", 1.0, "fas"],
    "fee-remote-work": [FontAwesomeIcon, "house-laptop"],
    "fee-other": [FontAwesomeIcon, "money-bill"],
    hourglass: [FontAwesomeIcon, "hourglass"],
    images: [FontAwesomeIcon, "images"],
    globe: [FontAwesomeIcon, "globe"],
    folder: [FontAwesomeIcon, "folder"],
    "square-user": [FontAwesomeIcon, "square-user"],
    __empty__: [EmptyIcon, "close"],
    __none__: [NoneIcon, "close"],
    "snackbar-info": [FontAwesomeIcon, "circle-info"],
    "snackbar-error": [FontAwesomeIcon, "message-xmark"],
    "bank-account": [FontAwesomeIcon, "building-columns"],
    "civil-state": [FontAwesomeIcon, "shield-check"],
    lpp: [FontAwesomeIcon, "shield-halved"],
    cct: [FontAwesomeIcon, "shield-halved", 1, "fas"],
    children: [HumanAndChildHoldingHandsComponent, "0"],
    upload: [FontAwesomeIcon, "upload"],
    "cloud-upload": [FontAwesomeIcon, "arrow-up-to-line"],
    "cloud-upload-outline": [FontAwesomeIcon, "cloud-arrow-up"],
    "cancel-mission": [FontAwesomeIcon, "flag-checkered"],
    "wifi-slash": [FontAwesomeIcon, "wifi-slash"],
    "address-card": [FontAwesomeIcon, "address-card"],
    passport: [FontAwesomeIcon, "passport"],
    "work-permit": [WorkPermitIconComponent, "wifi-slash"],
    receipt: [FontAwesomeIcon, "receipt"],
    hotel_clothes: [FontAwesomeIcon, "user-chef"],
    hotel_apron: [FontAwesomeIcon, "user-tie-hair-long", 1, "fal"],
    payroll_breakdown: [FontAwesomeIcon, "calculator"],
    tag: [FontAwesomeIcon, "tag"],
    buildings: [FontAwesomeIcon, "buildings"],
    hotel: [FontAwesomeIcon, "hotel"],
    ijm: [FontAwesomeIcon, "shield-virus", 1, "fal"],
    activities: [FontAwesomeIcon, "wave-pulse", 1, "fal"],
    phone: [FontAwesomeIcon, "phone", 1, "fal"],
    circle: [FontAwesomeIcon, "circle", 1],
    "lightbulb-on": [FontAwesomeIcon, "lightbulb-on"],
    lightbulb: [FontAwesomeIcon, "lightbulb"],
    fairy: [FontAwesomeIcon, "person-dress-fairy"],
    legal_employer: [FontAwesomeIcon, "scale-balanced", 1],
    write: [FontAwesomeIcon, "pen-to-square"],
    marriage: [FontAwesomeIcon, "rings-wedding"],
    moving: [FontAwesomeIcon, "person-dolly"],
    death_close_relative: [FontAwesomeIcon, "heart-crack"],
    sickness_close_relative: [FontAwesomeIcon, "stethoscope"],
    gear: [FontAwesomeIcon, "gear"],
};

// eslint-disable-next-line react/display-name
const Icon = forwardRef<View, IconProps>(
    ({ name, size = 16, color, style, iconPadding = 0, onLayout }, ref) => {
        if (!NAME_MAP[name]) {
            throw new Error(`Unable to find icon with name "${name}"`);
        }
        if (name === "__none__") {
            return null;
        }
        const [Component, iconName, factor = 1.0, iconPrefix = "fal"] = NAME_MAP[name];
        const icon: [IconPrefix, FaIconName] = [iconPrefix, iconName];
        let iconSize = size ? Math.round(size * factor) : undefined;
        if (iconSize && iconPadding) {
            iconSize -= iconPadding * 2;
        }
        return (
            <View
                ref={ref}
                onLayout={onLayout}
                style={[
                    {
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        height: size,
                        width: size,
                    },
                    style,
                ]}
            >
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <Component
                    icon={icon}
                    {...(Platform.OS === "web" ? { fontSize: iconSize } : { size: iconSize })}
                    color={color}
                />
            </View>
        );
    },
);

export const hasIcon = (name: string): name is IconName => {
    return IconNameList.includes(name as IconName);
};

export default Icon;

library.add(fal);
library.add(fas);
